// Anita Week 8 / Day 3 — Animals: Groups, Traits and Habitats.
// Verbatim from FINAL Week 8 Part A source-of-truth package. No invention.
// SAFETY: paper mini-zoo only; parentGuide explicitly forbids approaching
// or touching real / dead animals, insects, nests, feathers, droppings.

const day3 = {
  id: 'day-3',
  studentId: 'anita',
  weekId: 'week-8',
  dayId: 'day-3',
  status: 'active',
  title: 'Животные: группы, признаки и среда обитания / Animals: Groups, Traits and Habitats',
  shortTitle: 'Животные и среда / Animals and Habitats',
  theme:
    'Анита учится группировать животных, сравнивать признаки и понимать, что среда обитания помогает животному выживать. / Anita learns to group animals, compare traits, and understand that a habitat helps an animal survive.',
  estimatedTimeLabel: '60–90 минут / 60–90 minutes',
  lessonTiming: { minMinutes: 60, maxMinutes: 90, breaks: 2 },
  bilingualMode: 'ru-primary-en-helper',
  courseLayers: ['reading', 'math', 'science', 'animals', 'habitats', 'classification', 'data', 'spelling', 'creative', 'speech'],
  readingLevel: 'grade-2',
  nimnyIntro:
    'Нимни открыла мини-зоопарк на бумаге. Там есть птицы, рыбы, насекомые, звери, земноводные и пресмыкающиеся. Сегодня Анита узнает, что животных можно группировать по признакам и среде обитания. / Nimny opens a paper mini-zoo. There are birds, fish, insects, mammals, amphibians and reptiles. Today Anita learns that animals can be grouped by traits and habitats.',
  goals: [
    'Узнать группы животных. / Learn animal groups.',
    'Сравнивать признаки животных. / Compare animal traits.',
    'Понимать среду обитания. / Understand habitats.',
    'Читать таблицу животных. / Read an animal data table.',
    'Решать задачи на группы, умножение, деление и сравнение. / Solve grouping, multiplication, division and comparison tasks.',
    'Сделать карточку животного. / Make an animal card.'
  ],
  vocabulary: [
    { term: 'животное / animal', definition: 'живой организм. / a living organism.' },
    { term: 'группа / group', definition: 'несколько объектов с похожими признаками. / objects with similar traits.' },
    { term: 'признак / trait', definition: 'особенность, по которой узнают объект. / a feature used to identify.' },
    { term: 'среда обитания / habitat', definition: 'место, где животное живёт. / where an animal lives.' },
    { term: 'птица / bird', definition: 'животное с перьями. / an animal with feathers.' },
    { term: 'рыба / fish', definition: 'животное, которое обычно живёт в воде и имеет плавники. / an animal in water with fins.' },
    { term: 'насекомое / insect', definition: 'маленькое животное, обычно с 6 ногами. / a small animal usually with 6 legs.' },
    { term: 'зверь / mammal', definition: 'животное, которое кормит детёнышей молоком. / a mammal that feeds young with milk.' },
    { term: 'земноводное / amphibian', definition: 'животное, которое может жить у воды и на суше. / an animal that lives near water and on land.' },
    { term: 'пресмыкающееся / reptile', definition: 'животное с чешуёй, например ящерица или черепаха. / a reptile with scales.' }
  ],
  onlineTasks: [
    {
      id: 'reading-words',
      type: 'text',
      title: 'Задание 1 — Слова животных / Animal Words',
      prompt: 'Прочитай по-русски:\nживотное\nгруппа\nпризнак\nсреда обитания\nптица\nрыба\nнасекомое\nзверь\nземноводное\nпресмыкающееся',
      ruPrimaryText: 'Прочитай 10 русских слов животных.',
      enHelpText: 'Read the Russian animal words first. English is only for help.',
      answer: 'Анита пробует читать русские слова. / Anita attempts the Russian reading.'
    },
    {
      id: 'reading-sentences',
      type: 'text',
      title: 'Задание 2 — Маленькое чтение / Short Reading',
      prompt: 'Прочитай по-русски:\nЖивотных можно делить на группы.\nПризнаки помогают узнать группу.\nСреда обитания — это место, где животное живёт.\nНекоторые животные лучше выживают в своей среде.',
      ruPrimaryText: 'Прочитай 4 короткие фразы про группы и среду обитания.',
      enHelpText: 'Animals can be sorted into groups. Traits help identify a group. Habitat is where an animal lives. Some animals survive better in their habitat.',
      answer: 'Анита пробует читать 4 фразы. / Anita attempts the short reading.'
    },
    {
      id: 'animal-group-match',
      type: 'text',
      title: 'Задание 3 — Группы животных / Animal Groups',
      prompt:
        'Соедини группу и признак.\n\n1. птица\n2. рыба\n3. насекомое\n4. зверь\n5. земноводное\n6. пресмыкающееся\n\nA. обычно имеет 6 ног\nB. имеет перья\nC. живёт в воде и имеет плавники\nD. кормит детёнышей молоком\nE. может жить у воды и на суше\nF. имеет чешую, например ящерица',
      ruPrimaryText: 'Соедини 6 групп животных с их признаками.',
      enHelpText: 'Match 6 animal groups with their traits.',
      answer: '1-B, 2-C, 3-A, 4-D, 5-E, 6-F.'
    },
    {
      id: 'habitat-sort',
      type: 'classify',
      title: 'Задание 4 — Кто где живёт? / Who Lives Where?',
      prompt:
        'Разложи животных по среде обитания.\n\nКатегории:\nлес\nводоём\nлуг или сад\nпустыня или сухое место\n\nКарточки:\n1. белка\n2. рыба\n3. лягушка\n4. бабочка\n5. ящерица\n6. утка\n7. лиса\n8. пчела\n9. черепаха\n10. стрекоза\n11. заяц\n12. муравей',
      ruPrimaryText: 'Разложи 12 животных по 4 средам обитания.',
      enHelpText: 'Sort 12 animals into forest / waterbody / meadow-or-garden / desert-or-dry.',
      categories: [
        { id: 'forest', label: 'Лес / Forest' },
        { id: 'water', label: 'Водоём / Waterbody' },
        { id: 'meadow', label: 'Луг или сад / Meadow or garden' },
        { id: 'dry', label: 'Пустыня или сухое место / Desert or dry place' }
      ],
      items: [
        { id: 'i1', label: 'белка', category: 'forest' },
        { id: 'i2', label: 'рыба', category: 'water' },
        { id: 'i3', label: 'лягушка', category: 'water' },
        { id: 'i4', label: 'бабочка', category: 'meadow' },
        { id: 'i5', label: 'ящерица', category: 'dry' },
        { id: 'i6', label: 'утка', category: 'water' },
        { id: 'i7', label: 'лиса', category: 'forest' },
        { id: 'i8', label: 'пчела', category: 'meadow' },
        { id: 'i9', label: 'черепаха', category: 'water' },
        { id: 'i10', label: 'стрекоза', category: 'water' },
        { id: 'i11', label: 'заяц', category: 'forest' },
        { id: 'i12', label: 'муравей', category: 'meadow' }
      ],
      answer: 'лес: 1,7,11. водоём: 2,3,6,9,10. луг/сад: 4,8,12. пустыня/сухое: 5. Parent note: некоторые животные могут жить в разных средах — главное объяснить выбор.'
    },
    {
      id: 'math-animal-table',
      type: 'text',
      title: 'Задание 5 — Таблица животных / Animal Table',
      prompt:
        'Нимни записала животных:\n\nптицы — 8\nрыбы — 12\nнасекомые — 15\nзвери — 6\nземноводные — 4\nпресмыкающиеся — 5\n\nОтветь:\n1. Сколько всего птиц и рыб?\n2. Сколько всего земноводных и пресмыкающихся?\n3. На сколько насекомых больше, чем зверей?\n4. Сколько всего животных в таблице?',
      ruPrimaryText: 'Прочитай таблицу из 6 групп и реши 4 задачи.',
      enHelpText: '8 birds, 12 fish, 15 insects, 6 mammals, 4 amphibians, 5 reptiles. Answer 4 questions.',
      answer: '1) 8 + 12 = 20. 2) 4 + 5 = 9. 3) 15 − 6 = 9. 4) 8 + 12 + 15 + 6 + 4 + 5 = 50.'
    },
    {
      id: 'math-animal-arrays',
      type: 'text',
      title: 'Задание 6 — Животные в группах / Animal Arrays',
      prompt:
        'На карточках Нимни:\n\n5 рядов птиц, в каждом ряду по 4 птицы.\n3 ряда рыб, в каждом ряду по 6 рыб.\n2 ряда зверей, в каждом ряду по 5 зверей.\n\nОтветь:\n1. Сколько птиц?\n2. Сколько рыб?\n3. Сколько зверей?\n4. Сколько всего животных на карточках?\n5. Если 48 животных разделить поровну на 6 страниц атласа, сколько животных на каждой странице?',
      ruPrimaryText: 'Реши 5 задач на умножение и деление с животными.',
      enHelpText: '5×4 birds, 3×6 fish, 2×5 mammals. 48 ÷ 6. Answer 5 questions.',
      answer: '1) 5 × 4 = 20. 2) 3 × 6 = 18. 3) 2 × 5 = 10. 4) 20 + 18 + 10 = 48. 5) 48 ÷ 6 = 8.'
    },
    {
      id: 'habitat-evidence',
      type: 'text',
      title: 'Задание 7 — Подходит ли среда? / Does the Habitat Fit?',
      prompt:
        'Прочитай 4 ситуации.\n\n1. Рыба живёт в воде.\n2. Белка живёт в лесу, где есть деревья.\n3. Кактус растёт в сухом месте.\n4. Лягушка живёт рядом с водой.\n\nОтветь:\nПочему среда помогает животному или растению?\nВыбери 2 примера и объясни коротко.',
      ruPrimaryText: 'Выбери 2 примера и объясни, почему среда помогает выжить.',
      enHelpText: 'Pick 2 examples and explain why the habitat helps the organism survive.',
      answer: 'Open-ended. Accept if Anita explains that water, trees, dry place, or pond helps the organism survive.'
    },
    {
      id: 'data-animal-graph',
      type: 'text',
      title: 'Задание 8 — Мини-график животных / Animal Mini Graph',
      prompt:
        'Данные:\n\nптицы — 4\nрыбы — 6\nзвери — 3\nнасекомые — 8\n\nНарисуй квадратики:\nптицы: □ □ □ □\nрыбы: □ □ □ □ □ □\nзвери: □ □ □\nнасекомые: □ □ □ □ □ □ □ □\n\nОтветь:\n1. Какая группа самая большая?\n2. Какая группа самая маленькая?\n3. На сколько насекомых больше, чем зверей?',
      ruPrimaryText: 'Прочитай мини-график и ответь на 3 вопроса.',
      enHelpText: 'Read the mini bar graph and answer 3 questions.',
      answer: '1) Насекомые. 2) Звери. 3) 8 − 3 = 5.'
    },
    {
      id: 'spelling-animals',
      type: 'text',
      title: 'Задание 9 — Правописание / Spelling',
      prompt: 'Напиши:\nптица\nрыба\nзверь\nлес\nвода\n\nВставь буквы:\nпт_ца\nр_ба\nзв_рь\nл_с\nв_да',
      ruPrimaryText: 'Напиши 5 слов животных и среды и вставь пропущенные буквы.',
      enHelpText: 'Write 5 words and fill in the missing letters.',
      answer: 'птица, рыба, зверь, лес, вода.'
    },
    {
      id: 'creative-animal-card',
      type: 'text',
      title: 'Задание 10 — Карточка животного / Animal Card',
      prompt:
        'Сделай карточку животного.\n\nВыбери животное из списка:\nптица, рыба, бабочка, белка, лягушка, ящерица.\n\nНарисуй животное.\nЗаполни:\n\nМоё животное: ______.\nГруппа: ______.\nСреда обитания: ______.\nПризнак: ______.',
      ruPrimaryText: 'Выбери одно животное, нарисуй и подпиши группу, среду и признак.',
      enHelpText: 'Pick one animal, draw it and label group, habitat, trait.',
      hint: 'Используй рисунок — не подходи к диким животным. / Use a drawing; do not approach wild animals.',
      answer: 'Open-ended. Accept if Anita chooses an animal and labels group, habitat and trait.'
    },
    {
      id: 'speech-animal-explain',
      type: 'text',
      title: 'Задание 11 — Объясни родителю / Explain to Parent',
      prompt: 'Скажи родителю по-русски:\nЖивотных можно делить на группы.\nПризнаки помогают узнать группу.\nСреда обитания помогает животному жить.\nМоё животное: ______.',
      ruPrimaryText: 'Объясни родителю группу, признак и среду своего животного по-русски.',
      enHelpText: 'Tell a parent in Russian: group, trait, habitat of your chosen animal.',
      answer: 'Accept if Anita explains group/trait/habitat in Russian with support.'
    }
  ],
  notebookInstructions: [
    'Если нет принтера:',
    '1. Напиши слова дня.',
    '2. Прочитай 4 фразы.',
    '3. Соедини группы животных и признаки.',
    '4. Разложи животных по среде обитания.',
    '5. Реши задачи по таблице и рядам.',
    '6. Нарисуй мини-график.',
    '7. Сделай карточку животного.',
    '8. Объясни родителю группу, признак и среду.'
  ],
  printWorksheet: {
    title: 'Животные: группы, признаки и среда обитания / Animals: Groups, Traits and Habitats',
    subtitle: 'Неделя 8 · День 3 / Week 8 · Day 3',
    pages: [
      {
        title: 'Маршрут дня / Route',
        blocks: [
          { type: 'text', text: 'Имя: __________________________   Дата: __________________________' },
          { type: 'text', text: 'Миссия: Нимни открывает бумажный мини-зоопарк. Сегодня ты группируешь животных, считаешь данные и делаешь карточку животного. / Nimny opens a paper mini-zoo. Today you group animals, count data and make an animal card.' },
          { type: 'text', text: 'Задание 1 — Слова животных. Прочитай и обведи:' },
          { type: 'list', items: [
            'животное / animal',
            'группа / group',
            'признак / trait',
            'среда обитания / habitat',
            'птица / bird',
            'рыба / fish',
            'насекомое / insect',
            'зверь / mammal',
            'земноводное / amphibian',
            'пресмыкающееся / reptile'
          ] },
          { type: 'text', text: 'Задание 2 — Маленькое чтение. Поставь галочку:' },
          { type: 'list', items: [
            '□ Животных можно делить на группы.',
            '□ Признаки помогают узнать группу.',
            '□ Среда обитания — это место, где животное живёт.',
            '□ Некоторые животные лучше выживают в своей среде.'
          ] },
          { type: 'text', text: 'Задание 3 — Группы животных. Подсказки: A. обычно имеет 6 ног. B. имеет перья. C. живёт в воде и имеет плавники. D. кормит детёнышей молоком. E. может жить у воды и на суше. F. имеет чешую, например ящерица.' },
          { type: 'list', items: [
            '1. птица → ______',
            '2. рыба → ______',
            '3. насекомое → ______',
            '4. зверь → ______',
            '5. земноводное → ______',
            '6. пресмыкающееся → ______'
          ] }
        ]
      },
      {
        title: 'Среда и математика / Habitat and Math',
        blocks: [
          { type: 'text', text: 'Задание 4 — Кто где живёт? Напиши Л = лес, В = водоём, С = сад/луг, П = пустыня или сухое место.' },
          { type: 'list', items: [
            '1. белка ____',
            '2. рыба ____',
            '3. лягушка ____',
            '4. бабочка ____',
            '5. ящерица ____',
            '6. утка ____',
            '7. лиса ____',
            '8. пчела ____',
            '9. черепаха ____',
            '10. стрекоза ____',
            '11. заяц ____',
            '12. муравей ____'
          ] },
          { type: 'text', text: 'Задание 5 — Таблица животных. Птицы — 8, рыбы — 12, насекомые — 15, звери — 6, земноводные — 4, пресмыкающиеся — 5.' },
          { type: 'list', items: [
            '1. птицы + рыбы = ______',
            '2. земноводные + пресмыкающиеся = ______',
            '3. насекомые − звери = ______',
            '4. всего животных = ______'
          ] },
          { type: 'text', text: 'Задание 6 — Животные в группах (умножение и деление):' },
          { type: 'list', items: [
            '5 рядов птиц × 4 птицы = ______',
            '3 ряда рыб × 6 рыб = ______',
            '2 ряда зверей × 5 зверей = ______',
            'Всего животных: ______ + ______ + ______ = ______',
            '48 животных ÷ 6 страниц = ______'
          ] }
        ]
      },
      {
        title: 'Карточка животного / Animal Card',
        blocks: [
          { type: 'text', text: 'Задание 7 — Подходит ли среда? Выбери 2 примера и объясни. 1. Рыба живёт в воде. 2. Белка живёт в лесу, где есть деревья. 3. Кактус растёт в сухом месте. 4. Лягушка живёт рядом с водой.' },
          { type: 'list', items: [
            'Пример 1: __________________________________________',
            'Почему подходит: __________________________________________',
            'Пример 2: __________________________________________',
            'Почему подходит: __________________________________________'
          ] },
          { type: 'text', text: 'Задание 8 — Мини-график животных. птицы: □ □ □ □ ; рыбы: □ □ □ □ □ □ ; звери: □ □ □ ; насекомые: □ □ □ □ □ □ □ □.' },
          { type: 'list', items: [
            'Самая большая группа: ______',
            'Самая маленькая группа: ______',
            'Насекомых больше, чем зверей, на: ______'
          ] },
          { type: 'text', text: 'Задание 9 — Правописание. Напиши и вставь буквы:' },
          { type: 'list', items: [
            'птица   рыба   зверь   лес   вода',
            'пт_ца     р_ба     зв_рь     л_с     в_да'
          ] },
          { type: 'text', text: 'Задание 10 — Карточка животного. Выбери из: птица, рыба, бабочка, белка, лягушка, ящерица.' },
          { type: 'list', items: [
            'Моё животное: __________________________',
            'Группа: __________________________',
            'Среда обитания: __________________________',
            'Признак: __________________________'
          ] },
          { type: 'text', text: 'Нарисуй животное: __________________________________________________________' },
          { type: 'text', text: 'Задание 11 — Объясни родителю. Скажи:' },
          { type: 'list', items: [
            '□ Животных можно делить на группы.',
            '□ Признаки помогают узнать группу.',
            '□ Среда обитания помогает животному жить.',
            '□ Моё животное: __________________________'
          ] },
          { type: 'text', text: 'Parent Check: □ прочитала слова  □ прочитала 4 фразы  □ соединила группы и признаки  □ разложила животных по среде  □ решила math tasks  □ сделала карточку животного  □ объяснила родителю.' },
          { type: 'text', text: 'Notebook Mode fallback — если нет принтера, сделай все 11 заданий в тетради по тем же номерам.' }
        ]
      }
    ]
  },
  parentGuide: {
    goal: 'Анита понимает, что животных можно группировать по признакам и среде обитания.',
    mathGoal: 'Сложение нескольких чисел, вычитание, умножение рядов (5 × 4, 3 × 6, 2 × 5), деление 48 ÷ 6, таблица данных и график.',
    russianGoal: 'Прочитать слова животное, группа, признак, среда обитания, птица, рыба, насекомое, зверь, земноводное, пресмыкающееся.',
    readingWritingGoal: 'Прочитать 4 фразы, написать 5 слов, заполнить карточку животного.',
    practicalGoal: 'Сгруппировать животных, объяснить признак и среду обитания.',
    parentTheoryCard: {
      title: 'Как объяснить группы животных и среду обитания',
      purpose: 'Помочь родителю объяснить классификацию животных без перегруза и связать животное со средой.',
      keyMessage: 'Животных можно группировать по признакам, а среда обитания помогает понять, где животному удобно жить.',
      parentScript: [
        '«Группа — это животные с похожими признаками.»',
        '«Птица имеет перья.»',
        '«Рыба обычно живёт в воде и имеет плавники.»',
        '«Насекомые обычно имеют 6 ног.»',
        '«Среда обитания — место, где животное живёт и находит то, что ему нужно.»'
      ],
      practiceSteps: [
        'Соедините группы и признаки.',
        'Разложите животных по среде.',
        'Выберите одно животное.',
        'Назовите группу, признак и среду.',
        'Попросите Аниту объяснить по-русски.'
      ],
      doNotSay: [
        'Не требовать сложной зоологии.',
        'Не спорить о редких исключениях.',
        'Не предлагать трогать диких животных.'
      ],
      paperOnlyNotes: [
        'Можно использовать рисунки и выдуманный мини-зоопарк.',
        'Не нужно искать животных на улице.',
        'Реальные наблюдения только с родителем.'
      ]
    },
    whatThisLessonTeaches: 'Анита учится классифицировать животных и связывать признаки со средой.',
    keyMessage: 'Признак помогает узнать группу, а среда помогает понять, где животное живёт.',
    howToExplain: 'Скажите: «Мы не просто называем животное. Мы спрашиваем: какие у него признаки и где оно живёт?»',
    exampleDialogue:
      'Parent: У птицы есть что?\nChild: Перья.\nParent: Где живёт рыба?\nChild: В воде.\nParent: Почему белке подходит лес?\nChild: Там деревья.',
    readingSupport: 'Слова «среда обитания», «земноводное», «пресмыкающееся» читать по частям.',
    russianSpeechGoal: 'Анита говорит: «Моё животное — ___. Его группа — ___. Его среда — ___.»',
    mathAnswerKey:
      'math-animal-table: 1) 20. 2) 9. 3) 9. 4) 50.\nmath-animal-arrays: 1) 20. 2) 18. 3) 10. 4) 48. 5) 8.\ndata-animal-graph: 1) Насекомые. 2) Звери. 3) 5.',
    spellingAnswerKey: 'птица\nрыба\nзверь\nлес\nвода',
    answerKey: [
      { taskId: 'reading-words', answer: 'Попытка русского чтения 10 слов.' },
      { taskId: 'reading-sentences', answer: 'Попытка русского чтения 4 фраз.' },
      { taskId: 'animal-group-match', answer: '1-B, 2-C, 3-A, 4-D, 5-E, 6-F.' },
      { taskId: 'habitat-sort', answer: 'Лес 1,7,11; водоём 2,3,6,9,10; луг/сад 4,8,12; сухое 5.' },
      { taskId: 'math-animal-table', answer: '20; 9; 9; 50.' },
      { taskId: 'math-animal-arrays', answer: '20; 18; 10; 48; 8.' },
      { taskId: 'habitat-evidence', answer: 'Open-ended объяснение почему среда подходит.' },
      { taskId: 'data-animal-graph', answer: 'Насекомые; звери; 5.' },
      { taskId: 'spelling-animals', answer: 'птица, рыба, зверь, лес, вода.' },
      { taskId: 'creative-animal-card', answer: 'Карточка: животное, группа, среда, признак.' },
      { taskId: 'speech-animal-explain', answer: 'Объяснение группы, признака и среды по-русски.' }
    ],
    check: [
      'Анита прочитала слова и фразы.',
      'Анита соединила группы и признаки.',
      'Анита разложила животных по среде.',
      'Анита решила math tasks.',
      'Анита сделала карточку животного.',
      'Анита объяснила признак и среду.'
    ],
    spellingCheck: 'Проверить птица, рыба, зверь, лес, вода.',
    readingCheck: 'Главное — попытка и понимание.',
    practicalCheck: 'Анита должна объяснить хотя бы одно животное: группа + признак + среда.',
    drawingCriteria: 'Карточка содержит животное, группу, среду и признак.',
    commonMistakes: [
      '«Все животные живут где угодно.» Правильно: «Среда помогает животному жить.»',
      '«Птица — потому что летает.» Правильно: «Главный признак птиц — перья. Не все птицы хорошо летают.»',
      '«Насекомое — всё маленькое.» Правильно: «У насекомых обычно 6 ног.»'
    ],
    discussionPrompts: [
      'Какой признак у птицы?',
      'Почему рыбе нужна вода?',
      'Почему белке подходит лес?',
      'Какое животное ты выбрала?'
    ],
    ifEasy: 'Попросить Аниту добавить 3 новых животных и объяснить группу, признак и среду.',
    ifDifficult: 'Оставить только 4 группы: птицы, рыбы, насекомые, звери.',
    rewardGuidance: 'Базовая награда — 3 нима. Бонус за ясное объяснение животного или аккуратную карточку.',
    tokenReward: 'Нимы — это призовые токены курса, а не оплата.',
    parentOnlyNotes: 'Не искать и не трогать реальных животных ради урока. Использовать рисунки, карточки или воображаемый мини-зоопарк.',
    safetyNotes: 'Не подходить к диким или неизвестным животным. Не трогать насекомых, гнёзда, перья, следы или найденные предметы природы без родителя.'
  },
  reward: {
    unit: 'nim',
    unitName: 'ним',
    base: 3,
    max: 5,
    label: 'Награда за миссию',
    display: '3 нима',
    bonuses: [
      { id: 'clear-group-habitat', label: 'Бонус за ясное объяснение группы и среды', amount: 1 },
      { id: 'neat-animal-card', label: 'Бонус за аккуратную карточку животного', amount: 1 }
    ]
  },
  completionRequirements: {
    requiredTaskIds: [
      'reading-words',
      'reading-sentences',
      'animal-group-match',
      'habitat-sort',
      'math-animal-table',
      'math-animal-arrays',
      'habitat-evidence',
      'data-animal-graph',
      'spelling-animals',
      'creative-animal-card',
      'speech-animal-explain'
    ],
    allowNotebookCompletion: true
  },
  nextLessonId: 'anita-week-8-day-4'
};

export default day3;
